import { EntityLink } from '../../../navigation/navigation'
import React, { useEffect, useMemo, useState } from 'react'
import { useEntityNavigate as useNavigate } from '../../../navigation/navigation'
import api from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import { usePageHeader } from '../../../contexts/PageHeaderContext'
import Icon from '../../../components/Icon'
import { Button, Input, Select, TableShell } from '../../../components/ui'
import {
  ReportStatusBadge,
  REPORT_TYPE_INCOME,
  REPORT_TYPE_LABELS,
  formatDateTime,
  reportTitle,
} from './IncomeReportComponents'
import './Reports.css'

// Тип «Сводный бюджет» не хранится в БД как отчёт — это живая страница
// /company/stages/summary-report, поэтому при выборе мы просто переходим туда.
const REPORT_TYPE_SUMMARY_BUDGET = 'summary_budget'

function currentQuarter() {
  const now = new Date()
  return {
    year: now.getFullYear(),
    quarter: Math.ceil((now.getMonth() + 1) / 3),
  }
}

function hasPermission(user, permission) {
  return user?.permissions?.[permission] === true
}

function ReportsList() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const defaults = useMemo(() => currentQuarter(), [])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    type: REPORT_TYPE_INCOME,
    period_type: 'quarter',
    year: defaults.year,
    quarter: defaults.quarter,
    title: '',
  })

  const canCreate = hasPermission(user, 'reports.create')
  const existingPrimary = form.type === REPORT_TYPE_INCOME && reports.find((report) => (
    report.type === form.type && report.is_primary && !report.archived_at
    && report.period_type === form.period_type && report.year === Number(form.year)
    && (form.period_type === 'year' || report.quarter === Number(form.quarter))
  ))

  usePageHeader({
    title: 'Отчетность',
    renderActions: () => canCreate ? (
      <Button type="button" variant="primary" onClick={() => setCreateOpen(true)}>
        <Icon name="plus" size={16} /> Создать отчет
      </Button>
    ) : null,
  }, [canCreate])

  const fetchReports = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/company/reports')
      setReports(response.data?.reports || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось загрузить отчеты')
      setReports([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReports()
  }, [])

  const createReport = async (event) => {
    event.preventDefault()
    if (!canCreate) return
    setCreating(true)
    setError('')
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        quarter: form.period_type === 'quarter' ? Number(form.quarter) : null,
      }
      if (!payload.title.trim()) delete payload.title
      const response = await api.post('/company/reports', payload)
      const report = response.data?.report
      setCreateOpen(false)
      if (report?.id) {
        navigate(`/company/reports/${report.id}`)
      } else {
        await fetchReports()
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось создать отчет')
    } finally {
      setCreating(false)
    }
  }

  const years = Array.from({ length: 8 }, (_, index) => defaults.year + 1 - index)

  return (
    <div className="company-page reports-page">
      {error && <div className="message message-error reports-message">{error}</div>}

      <TableShell className="reports-list-shell">
        <div className="table-responsive">
          <table className="ui-table data-table reports-list-table">
            <thead>
              <tr>
                <th>Тип</th>
                <th>Период</th>
                <th>Статус</th>
                <th>Дата создания</th>
                <th>Дата редактирования</th>
                <th>Автор</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6}>Загрузка…</td></tr>
              )}
              {!loading && reports.length === 0 && (
                <tr>
                  <td colSpan={6} className="reports-empty-cell">
                    Пока нет активных отчетов
                  </td>
                </tr>
              )}
              {!loading && reports.map((report) => (
                <tr
                  key={report.id}
                  className="reports-list-row"
                  role="button"
                  tabIndex={0}
                  aria-label={`Открыть отчет: ${reportTitle(report)}`}
                  onClick={() => navigate(`/company/reports/${report.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      navigate(`/company/reports/${report.id}`)
                    }
                  }}
                >
                  <td>
                    <EntityLink to={`/company/reports/${report.id}`} className="reports-title-link">
                      {REPORT_TYPE_LABELS[report.type] || report.type}
                    </EntityLink>
                    <div className="reports-title-sub">{reportTitle(report)}</div>
                  </td>
                  <td>{report.period_label || '—'}</td>
                  <td><ReportStatusBadge report={report} /></td>
                  <td className="mono">{formatDateTime(report.created_at)}</td>
                  <td className="mono">{formatDateTime(report.updated_at)}</td>
                  <td>{report.author_display_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableShell>

      {createOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="create-report-title" onClick={() => setCreateOpen(false)}>
          <form className="modal-content reports-create-modal" onSubmit={createReport} onClick={(event) => event.stopPropagation()}>
            <h3 id="create-report-title">Новый отчет</h3>
            <label className="report-config-field">
              <span>Тип</span>
              <Select value={form.type} onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}>
                <option value={REPORT_TYPE_INCOME}>{REPORT_TYPE_LABELS[REPORT_TYPE_INCOME]}</option>
                <option value={REPORT_TYPE_SUMMARY_BUDGET}>Сводный бюджет</option>
              </Select>
            </label>
            <label className="report-config-field">
              <span>Название</span>
              <Input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Будет заполнено автоматически" />
            </label>
            <div className="reports-form-grid">
              <label className="report-config-field">
                <span>Период</span>
                <Select value={form.period_type} onChange={(event) => setForm((prev) => ({ ...prev, period_type: event.target.value }))}>
                  <option value="quarter">Квартал</option>
                  <option value="year">Год</option>
                </Select>
              </label>
              <label className="report-config-field">
                <span>Год</span>
                <Select value={form.year} onChange={(event) => setForm((prev) => ({ ...prev, year: Number(event.target.value) }))}>
                  {years.map((year) => <option key={year} value={year}>{year}</option>)}
                </Select>
              </label>
              {form.period_type === 'quarter' && (
                <label className="report-config-field">
                  <span>Квартал</span>
                  <Select value={form.quarter} onChange={(event) => setForm((prev) => ({ ...prev, quarter: Number(event.target.value) }))}>
                    <option value={1}>1 кв.</option>
                    <option value={2}>2 кв.</option>
                    <option value={3}>3 кв.</option>
                    <option value={4}>4 кв.</option>
                  </Select>
                </label>
              )}
            </div>
            {existingPrimary && (
              <p className="report-readonly-note" role="status">
                За {existingPrimary.period_label || (form.period_type === 'year' ? `${form.year} год` : `${form.quarter} кв. ${form.year}`)} уже есть основной отчёт «{reportTitle(existingPrimary)}». Новый отчёт будет создан черновиком.
              </p>
            )}
            <div className="modal-actions">
              <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Отмена</Button>
              <Button type="submit" variant="primary" disabled={creating}>{creating ? 'Создание…' : 'Создать'}</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

export default ReportsList
